import React, { useState, useEffect, useCallback } from "react";
import Styles from "./CompanyDetails.module.css";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAdminConsumerRaListing,
  fetchConsumerCapacities,
} from "../../../Redux/Slices/gnaAdminSlice";
import ListingDetails from "../../Modals/Admin/ListingDetails/ListingDetails";
import ListingTable from "./ListingTable";
import SchdeuleTable from "./SchdeuleTable";

const ConsumerDetails = ({ fullConsumerDetails }) => {
  const dispatch = useDispatch();
  const role = useSelector(
    (state) => state?.persistedReducer?.user?.activeRole
  );
  const consumerId = fullConsumerDetails?.consumerId;

  const [viewType, setViewType] = useState("details");
  const [listingView, setListingView] = useState({
    open: false,
    data: null,
  });

  // Selectors
  const bilateralListings = useSelector(
    (state) => state.gnaAdminSlice.fetchCapacity?.data?.page
  );

  const raListings = useSelector(
    (state) => state.gnaAdminSlice.fetchRaList?.data?.page
  );

  /**
   * Effect for fetching data when the viewType changes.
   */
  useEffect(() => {
    if (!consumerId) return;

    // Reset previous listings data when switching view types, if necessary
    // Note: We rely on the thunks to handle loading states/fresh data in the slice.

    switch (viewType) {
      case "bilateral":
        dispatch(fetchConsumerCapacities(consumerId));
        break;
      case "ra":
        // Fetch Auction Tenders (type: RA)
        dispatch(
          fetchAdminConsumerRaListing({
            id: consumerId,
            type: "RA",
          })
        );
        break;
      case "rfq":
        // Fetch RfQ Tenders (type: CB)
        dispatch(
          fetchAdminConsumerRaListing({
            id: consumerId,
            type: "CB",
          })
        );
        break;
      case "details":
      default:
        // No fetch needed for details view
        break;
    }
  }, [viewType, consumerId, dispatch]);

  /**
   * Helper component to render the main 'Details' section.
   */

  const renderSchedule = useCallback(() => {
    return (
      <SchdeuleTable
        id={consumerId}
        companyId={fullConsumerDetails?.companyId}
      />
    );
  }, [consumerId, fullConsumerDetails?.companyId]);

  const renderDetails = useCallback(() => {
    const details = [
      {
        header: "Consumer Full Details",
        items: [
          { label: "Consumer ID", value: fullConsumerDetails?.consumerId },
          { label: "Consumer Name", value: fullConsumerDetails?.consumerName },
          {
            label: "Consumer Type",
            value: fullConsumerDetails?.specificationName,
          },
        ],
      },
      {
        header: "Bank Details",
        items: [
          {
            label: "Bank Name",
            value: fullConsumerDetails?.consumerBankDetails?.bankName,
          },
          {
            label: "Bank Account Number",
            value: fullConsumerDetails?.consumerBankDetails?.accountNumber,
          },
          {
            label: "Bank Address",
            value: fullConsumerDetails?.consumerBankDetails?.branchAddress,
          },
          {
            label: "IFSC Code",
            value: fullConsumerDetails?.consumerBankDetails?.ifscCode,
          },
        ],
      },
      {
        header: "Office Address",
        items: [
          {
            label: "Address",
            value: fullConsumerDetails?.consumerOfficeAddress?.address,
          },
          {
            label: "Pincode",
            value: fullConsumerDetails?.consumerOfficeAddress?.pinCode,
          },
          {
            label: "City",
            value: fullConsumerDetails?.consumerOfficeAddress?.cityName,
          },
          {
            label: "State",
            value: fullConsumerDetails?.consumerOfficeAddress?.stateUtName,
          },
          {
            label: "Contact",
            value:
              fullConsumerDetails?.consumerOfficeAddress?.contactPersonName,
          },
          {
            label: "Email",
            value: fullConsumerDetails?.consumerOfficeAddress?.email,
          },
          {
            label: "Mobile",
            value: fullConsumerDetails?.consumerOfficeAddress?.mobileNumber,
          },
          {
            label: "Alt. Mobile",
            value:
              fullConsumerDetails?.consumerOfficeAddress?.alternateMobileNumber,
          },
        ],
      },
      {
        header: "Correspondence Address",
        items: [
          {
            label: "Address",
            value: fullConsumerDetails?.consumerCorrespondenceAddress?.address,
          },
          {
            label: "Pincode",
            value: fullConsumerDetails?.consumerCorrespondenceAddress?.pinCode,
          },
          {
            label: "City",
            value: fullConsumerDetails?.consumerCorrespondenceAddress?.cityName,
          },
          {
            label: "State",
            value:
              fullConsumerDetails?.consumerCorrespondenceAddress?.stateUtName,
          },
          {
            label: "Contact",
            value:
              fullConsumerDetails?.consumerCorrespondenceAddress
                ?.contactPersonName,
          },
          {
            label: "Email",
            value: fullConsumerDetails?.consumerCorrespondenceAddress?.email,
          },
          {
            label: "Mobile",
            value:
              fullConsumerDetails?.consumerCorrespondenceAddress?.mobileNumber,
          },
          {
            label: "Alt. Mobile",
            value:
              fullConsumerDetails?.consumerCorrespondenceAddress
                ?.alternateMobileNumber,
          },
        ],
      },
    ];

    return (
      <>
        {details.map((section, sectionIndex) => (
          <div
            key={sectionIndex}
            className={`display_flex_column_gap_8 ${Styles.detailsContainer}`}
          >
            <div className={Styles.sectionHeader}>{section.header}</div>
            <div className={`${Styles.detailRow}`}>
              {section.items.map((item, itemIndex) => (
                <div
                  key={itemIndex}
                  className={`display_flex_column_gap_8 ${Styles.detailItem}`}
                >
                  <div>{item.label}</div>
                  <div>{item.value || "-"}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </>
    );
  }, [fullConsumerDetails]);

  // Helper component to render the active view content
  const renderActiveView = () => {
    switch (viewType) {
      case "details":
        return renderDetails();
      case "schedule":
        return renderSchedule();
      case "bilateral":
        return (
          <ListingTable
            type="bilateral"
            data={bilateralListings}
            setListingView={setListingView}
          />
        );
      case "ra":
        return (
          <ListingTable
            type="ra"
            data={raListings}
            setListingView={setListingView}
          />
        );
      case "rfq":
        return (
          <ListingTable
            type="rfq"
            data={raListings} // RfQ uses the same reducer state fetchRaList
            setListingView={setListingView}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {listingView.open && (
        <ListingDetails
          open={listingView.open}
          handleClose={() => setListingView({ open: false, data: null })}
          data={listingView.data}
        />
      )}
      <div className={`display_flex_row_center ${Styles.viewTypeContainer}`}>
        <div
          onClick={() => setViewType("details")}
          className={viewType === "details" ? `activeButton` : ""}
        >
          Details
        </div>
        {(role === "MAKER" || role === "CHECKER") && (
          <div
            onClick={() => setViewType("schedule")}
            className={viewType === "schedule" ? `activeButton` : ""}
          >
            Payment
          </div>
        )}
        <div
          onClick={() => setViewType("bilateral")}
          className={viewType === "bilateral" ? `activeButton` : ""}
        >
          Bilateral Listings
        </div>
        <div
          onClick={() => setViewType("ra")}
          className={viewType === "ra" ? `activeButton` : ""}
        >
          Auction Tenders
        </div>
        <div
          onClick={() => setViewType("rfq")}
          className={viewType === "rfq" ? `activeButton` : ""}
        >
          Rfq Tenders
        </div>
      </div>
      <div className={`scroll_bar_y ${Styles.activeViewEntity}`}>
        {renderActiveView()}
      </div>
    </div>
  );
};

export default ConsumerDetails;
